import * as THREE from "three"
import { useGLTF, useTexture } from '@react-three/drei'
import { Canvas, useFrame, useThree } from 'react-three-fiber'
import create from "zustand"

import * as Tone from "tone"
import React, { Suspense, useRef, useCallback, useEffect, useMemo, useState } from "react"
import lerp from "lerp"
import { TweenMax, Sine, gsap } from "gsap"
import inst from "./inst"

import { Flock } from "./Flock";

let hitCount = 0
let isMobile = window.innerWidth < 500
let xWidth = isMobile ? 20 : 80

gsap.defaults({
  ease: Sine.easeOut
})
const col = 18
let getInst = () => {
  hitCount++
  return (hitCount % 2 === 0) ? inst.synthL : inst.synthR
}

let notes = ["C", "D", 'E', 'F', 'G', 'A']
let timeSteps = [0.2, 0.25, 0.2, 0.25, 0.35, 0.3, 0.5, 0.4]
let genPattern = () => {
  let t = 0
  return new Array(Math.ceil(Math.random() * 11)).fill().map((v, i) => {
    let dur = timeSteps[Math.floor(Math.random() * timeSteps.length)]
    let note = {
      i,
      note: notes[Math.floor(Math.random() * notes.length)] + ( Math.ceil(Math.random() * 5)),
      time: t,
      dur: dur,
      velocity: 0.5 + Math.random() * 3,
      first: (i === 0)
    }
    t += dur
    return note
  })
}

const [useStore] = create((set, get) => ({
  startTime: null,
  started: false,
  sequence: [],
  pattern0: genPattern(),
  pattern1: genPattern(),
  mutation: {
    parts: [],
    cursor: null,
    mouseDown: false,
    pageX: window.innerWidth / 2,
    pageY: window.innerHeight / 2,
    meter: 0,
    timeSpeed: 0,
    interval: '2',
    dir: 0,
    dirTarget: 0,
    locked: false,
    front: 1,
    accent: '8',
    noteLen: 0.5,
    started: false,
  },
  set: (key, val) => set(() => {
    return { [key]: val };
  }),
}))

function Rig() {
  const { camera, mouse } = useThree()
  const vec = new THREE.Vector3()
  const mutation = useStore(state => state.mutation)

  return useFrame(() => {
    camera.position.lerp(vec.set(-mouse.x * 6, -mouse.y * 6, camera.position.z), 0.)
    camera.lookAt(0, 0, 0)
    //if(!mutation.cursor) return

    mutation.meter = lerp(mutation.meter, Math.abs(inst.meterL.getValue() + inst.meterR.getValue()) * (isMobile ? 200 : 400), 0.05)
    mutation.noteLen = lerp(mutation.noteLen, 0.05, mutation.mouseDown ? 0.1 : 0.2)
    let size = (window.innerHeight / 24) + mutation.meter * 50
    mutation.cursor.current.style.left = mutation.pageX + 'px'
    mutation.cursor.current.style.top = window.innerHeight - mutation.pageY + 'px'
    mutation.cursor.current.style.width = size + 'px'
    mutation.cursor.current.style.height = size + 'px'

    mutation.inverter1.current.style.left = mutation.pageX + 'px'
    mutation.inverter1.current.style.top = (window.innerHeight - mutation.pageY) + 'px'
    mutation.inverter2.current.style.left = mutation.pageX + 'px'
    mutation.inverter2.current.style.top = (window.innerHeight - mutation.pageY) + 'px'
  })
}

function Loop({ mouse, i, sides }) {
  let s = 2
  let w = 0.05
  const pattern = useStore(state => state['pattern' + i])
  console.log('pattern', pattern)
  const mutation = useStore(state => state.mutation)
  const rings = useMemo(() => new Array(pattern.length).fill().map((v, i) => new THREE.TorusGeometry(s * (i * 0.5 + 0.5), w, 16, sides)), [pattern]);
  const mat = useMemo(() => new THREE.MeshStandardMaterial(), []);
  const ref = useRef();
  const flock = useRef();
  const currMouse = useRef([...mouse, true]);
  const tweens = useRef([]);
  useFrame(() => {
    let r = flock.current.rotation.x+mutation['flockRot'+i]
    //flock.current.rotation.set(r,0,0)
  })

  useEffect(() => {
    if (mutation.parts[i]) {
      currMouse.current[3] = false
      mutation.parts[i].stop()
      currMouse.current[2] = true
      setTimeout(() => {
        currMouse.current[2] = false
      }, 1000)
    }
    let part = new Tone.Part((function (time, value) {
      let s = getInst()
      s.triggerAttackRelease(value.note, value.dur*((5 - value.note[1])*0.4), time, value.velocity);
      if (value.first) {
        let last = 0
        pattern.forEach((note, j) => {
          ref.current.children[j] && (tweens.current[0] = TweenMax.fromTo(ref.current.children[j].rotation, (note.time + note.dur) * 0.8, 
          {x: 0},
          {
            x: Math.PI,
            delay: (note.time + note.dur) * 0.2,
            ease: Sine.easeOut,
            onComplete: () => {
              if (!ref.current.children[j]) return
              ref.current.children[j].scale.setScalar(1.3)
              tweens.current[1] = TweenMax.to(ref.current.children[j].scale, 0.4, {
                x: 1,
                y: 1,
                z: 1,
                ease: Sine.easeOut
              })
            }
          })
        )
          last = note.time + note.dur
        })
        TweenMax.fromTo(ref.current.rotation, last, {y: ref.current.rotation.y}, {
          y: ref.current.rotation.y + Math.PI,
          ease: Sine.easeOut
        })
      }
      currMouse.current[2] = true
      setTimeout(() => {
        currMouse.current[2] = false
      }, value.dur * 100)
    }), pattern)
    
    part.loop = true
    part.loopEnd = pattern[pattern.length - 1].dur + pattern[pattern.length - 1].time
    part.start('+0.5')
    mutation.parts[i] = part
  }, [pattern]);

  const { size, viewport } = useThree();
  const aspect = size.width / viewport.width;

  useFrame(state => {
    let mousePosition = new THREE.Vector3(
      mouse[0] / aspect,
      -mouse[1] / aspect,
      0
    );
    ref.current.position.set(
      mousePosition.x,
      mousePosition.y,
      mousePosition.z
    );
    let size = 1 + mutation.meter * 0.1
    ref.current.scale.set(
      size, size, size
    );
  });
  let s2 = 1.5
  let w2 = 0.2
  return (
    <>
      <ambientLight intensity={0.5} color={"white"} />
      <directionalLight intensity={1} color={"white"} />
      <group ref={ref} >
        {new Array(rings.length).fill().map((v, i) => {
          return <mesh
            key={i}
            geometry={rings[i]}
            material={mat}
            onClick={(e) => console.log('up')}
            onPointerDown={(e) => console.log('down')}
          >
          </mesh>
        })
        }
      </group>
      <group ref={flock}>
        <Flock mouse={currMouse} count={isMobile ? 100 : 100} geo={new THREE.TorusGeometry(s2 * (0.5 + 1), w2, 16, sides)} />
      </group>
    </>
  );
}

export default function () {
  const mutation = useStore(state => state.mutation)
  const set = useStore(state => state.set)
  const inverter1 = useRef()
  const inverter2 = useRef()
  const inverter3 = useRef()
  const cursor = useRef()
  const timeout = useRef()
  const randomizer = useRef()
  const inverted = useRef(false)

  const mouse = useRef([0, 0, false]);

  const onMouseMove = useCallback((e) => {
    mouse.current = [e.clientX - window.innerWidth / 2, e.clientY - window.innerHeight / 2, mouse.current[2]];
    if (e.buttons === 0) {
      mutation.mouseDown = false
    }
    mutation.pageX = e.pageX
    mutation.pageY = window.innerHeight - e.pageY
  },
    []
  );

  const handleMouseDown = event => {
    if (mutation.locked) return
    mutation.locked = true
    setTimeout(() => {
      mutation.locked = false
    }, 1200)
    //mutation.parts.map((p, i) => p.start())
    Tone.Transport.start();
    set('pattern0', genPattern())
    set('pattern1', genPattern())
    if (event.button !== 2) {
      mouse.current[2] = true;
    }
    play(5)
    document.body.style.cursor = "none";
    mutation.mouseDown = true
    mutation.started = true
    randomize()
    clearTimeout(timeout.current)
    clearInterval(randomizer.current)
    randomizer.current = setInterval(() => {
      randomize()
    }, 60000)
  };

  const handleMouseUp = event => {
    if (event.button !== 2) {
      mouse.current[2] = false;
    }
    mutation.mouseDown = false
  };

  useEffect(() => {
    setInterval(() => {
      if (mouse.current[2]) return
      mouse.current[2] = true
      timeout.current = setTimeout(() => {
        mouse.current[2] = false
      }, 600)
    }, 2000)

    mutation.inverter1 = inverter1
    mutation.inverter2 = inverter2
    mutation.cursor = cursor
  }, [])

  const play = useCallback((vel) => {
    let s = getInst()
    let hit = 0
    let punch = 0
    mutation.meter = mutation.meter + vel * 0.2
    mutation.noteLen = Math.min((mutation.noteLen + (vel)), 3)
    for (let i = 0; i < mutation.accent.length; i++) {
      if (hitCount % mutation.accent[i] === 0) {
        hit = 1200 / (mutation.accent[i])
        punch = 0.2
        //break;
      }
    }
    let freq = 30 + (isMobile ? (mutation.pageX * 1.75) : (mutation.pageY * 1.2)) + hit
    s.triggerAttackRelease(freq * 0.3, mutation.noteLen, '+0.01', vel + (Math.random() * 0.2) + punch);
  }, [])
  const newAccent = useCallback(() => {
    mutation.accent = ''
    new Array(9).fill().forEach((v, i) => Math.random() > 0.9 ? mutation.accent += (i + 1) : null)
  }, [])

  let ramps = ['linearRampTo', 'exponentialRampTo']
  const randomize = useCallback((vel) => {
    mutation.dirTarget = (Math.random() * 3) - 1.5

    let inverterSize = isMobile ? '240vh' : '300vw'
    inverter1.current.style.width = '0vh'
    inverter1.current.style.height = '0vh'

    inverter2.current.style.width = '0vh'
    inverter2.current.style.height = '0vh'
    if (inverted.current) {
      TweenMax.to('.invert1', 1.2, {width: inverterSize, height: inverterSize })
      inverter3.current.style.width = '0vh'
      inverter3.current.style.height = '0vh'
    } else {
      TweenMax.to('.invert2', 1.2, {width: inverterSize, height: inverterSize })
      inverter3.current.style.width = inverterSize
      inverter3.current.style.height = inverterSize
    }
    inverted.current = !inverted.current

    let rampTime = 1

    inst.delayL.wet[ramps[Math.round(Math.random())]](Math.random() * 0.95, rampTime)
    inst.delayL.delayTime[ramps[Math.round(Math.random())]](Math.random() * 1, rampTime)
    inst.delayL.feedback[ramps[Math.round(Math.random())]](Math.random() * 0.1, rampTime)

    inst.delayR.wet[ramps[Math.round(Math.random())]](Math.random() * 0.95, rampTime)
    inst.delayR.delayTime[ramps[Math.round(Math.random())]](Math.random() * 1, rampTime)
    inst.delayR.feedback[ramps[Math.round(Math.random())]](Math.random() * 0.1, rampTime)
  }, [])
  return (
    <>
      <Canvas camera={{ position: [0, 0, isMobile ? 80 : 70], fov: 80, far: 500 }}
        /*onPointerMove={mouseMove}
        onMouseMove={mouseMove}
        onMouseUp={mouseUp}

        onPointerDown={mouseDown}
        onPointerUp={mouseUp}
        */
        onPointerDown={(e) => isMobile && handleMouseDown(e)}
        onPointerMove={onMouseMove}
        onPointerUp={handleMouseUp}
        onMouseMove={onMouseMove}
        onMouseDown={(e) => !isMobile && handleMouseDown(e)}
        onMouseUp={handleMouseUp}
        gl={{ antialias: true }}
        //pixelRatio={0.4 * window.devicePixelRatio}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping
          gl.outputEncoding = THREE.sRGBEncoding
        }}
      >
        <ambientLight intensity={0.4} />
        <spotLight
          position={[10, 10, 10]}
          angle={0.2}
          penumbra={1}
          intensity={0.2}
        />
        <Rig />
        <directionalLight position={[-1, 4, -2]} intensity={2} color="white" />
        <pointLight position={[-1, -4, -0]} intensity={3} color="white" />
        <Loop mouse={[-window.innerWidth * 0.15, -window.innerHeight * 0.15, false]} i={0} sides={100} />
        <Loop mouse={[window.innerWidth * 0.15, window.innerHeight * 0.15, false]} i={1} sides={4} />
      </Canvas>
      <div ref={inverter1} className='invert1' />
      <div ref={inverter2} className='invert2' />
      <div ref={inverter3} className='invert3' />
      <div ref={cursor} className="cursor" />
    </>
  )
}
/*
<Loop mouse={[-window.innerWidth*0.3,0,false]} i={0} sides={3} />
        <Loop mouse={[0,0,false]} i={1} sides={4} />
        <Loop mouse={[window.innerWidth*0.3,0,false]} i={2} sides={100} />
        */
console.log('%c🌱🌱 @ramioso 2021-05-25 🌱🌱 ', 'color: orange');